import { formatCurrency } from '@eriko/shared-types';
import { Badge } from '../ui/badge';

export function InvoiceDetailView({ data }: { data: Record<string, unknown> }) {
  const docNumber = String(data.DocNumber ?? data.externalId ?? '');
  const txnDate = data.TxnDate ? new Date(String(data.TxnDate)) : null;
  const dueDate = data.DueDate ? new Date(String(data.DueDate)) : null;
  const currencyName =
    (data.CurrencyRef as { name?: string } | undefined)?.name ?? 'United States Dollar';
  const customerName =
    (data.CustomerRef as { name?: string } | undefined)?.name ?? '';
  const billAddr = data.BillAddr as Record<string, string> | undefined;
  const billEmail = (data.BillEmail as { Address?: string } | undefined)?.Address;
  const customerMemo = (data.CustomerMemo as { value?: string } | undefined)?.value;
  const salesTerm = (data.SalesTermRef as { name?: string } | undefined)?.name;
  const totalAmt = Number(data.TotalAmt ?? 0);
  const balance = Number(data.Balance ?? totalAmt);
  const totalTax = Number(
    (data.TxnTaxDetail as { TotalTax?: number } | undefined)?.TotalTax ?? 0,
  );

  const lines = ((data.Line as Array<Record<string, unknown>>) ?? []).filter(
    (l) => l.DetailType === 'SalesItemLineDetail',
  );

  const formatDate = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const now = new Date();
  const isOverdue = dueDate && balance > 0 && now > dueDate;
  const daysOverdue = isOverdue
    ? Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const isPaid = balance === 0;

  const addressLines: string[] = [];
  if (billAddr) {
    if (billAddr.Line1) addressLines.push(billAddr.Line1);
    if (billAddr.Line2) addressLines.push(billAddr.Line2);
    if (billAddr.Line3) addressLines.push(billAddr.Line3);
    if (billAddr.Line4) addressLines.push(billAddr.Line4);
    if (!billAddr.Line3 && (billAddr.City || billAddr.CountrySubDivisionCode)) {
      addressLines.push(
        [billAddr.City, billAddr.CountrySubDivisionCode, billAddr.PostalCode]
          .filter(Boolean)
          .join(', '),
      );
    }
  }

  return (
    <div className="space-y-0">
      {/* Header: Invoice meta */}
      <div className="flex justify-between items-start mb-6 pb-5 border-b border-border/50">
        <div>
          <div
            className="text-[28px] text-foreground mb-2"
            style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontWeight: 400 }}
          >
            Invoice
          </div>
          {isPaid && (
            <Badge className="bg-green-500/10 text-green-700 border-0">Paid</Badge>
          )}
        </div>
        <div className="text-left">
          <div className="space-y-0">
            {[
              ['Number', docNumber],
              ['Issued', txnDate ? formatDate(txnDate) : '—'],
              ['Due', dueDate ? formatDate(dueDate) : '—'],
              ['Currency', currencyName],
            ].map(([label, value]) => (
              <div key={label} className="flex gap-2.5 py-[2px]">
                <span className="text-xs text-muted-foreground/70 w-[60px] flex-shrink-0">
                  {label}
                </span>
                <span className="font-mono text-xs font-medium text-foreground">
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Overdue badge */}
      {isOverdue && (
        <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-md mb-6 bg-amber-500/10 text-amber-700">
          <div className="w-[7px] h-[7px] rounded-full flex-shrink-0 bg-amber-600" />
          <span className="text-[13px] font-medium flex-1">
            {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue
          </span>
        </div>
      )}

      {/* Bill To */}
      {customerName && (
        <div className="mb-5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.8px] text-muted-foreground/70 mb-1.5">
            Bill To
          </div>
          <div className="text-sm font-semibold text-foreground mb-0.5">
            {customerName}
          </div>
          {addressLines.length > 0 && (
            <div className="text-[12.5px] leading-[1.65] text-muted-foreground">
              {addressLines.map((line, i) => (
                <span key={i}>
                  {line}
                  {i < addressLines.length - 1 && <br />}
                </span>
              ))}
            </div>
          )}
          {billEmail && (
            <div className="text-[12.5px] text-muted-foreground mt-0.5">{billEmail}</div>
          )}
        </div>
      )}

      {/* Line items table */}
      {lines.length > 0 && (
        <table className="w-full border-collapse mb-0">
          <thead>
            <tr>
              <th className="text-left text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70 py-2 border-b border-border">
                Description
              </th>
              <th className="text-center text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70 py-2 border-b border-border w-[50px]">
                Qty
              </th>
              <th className="text-right text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70 py-2 border-b border-border w-[80px]">
                Rate
              </th>
              <th className="text-right text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70 py-2 border-b border-border w-[90px]">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => {
              const detail = line.SalesItemLineDetail as Record<string, unknown> | undefined;
              const itemName =
                (detail?.ItemRef as { name?: string } | undefined)?.name ?? '';
              const description = String(line.Description ?? itemName);
              const qty = Number(detail?.Qty ?? 1);
              const unitPrice = Number(detail?.UnitPrice ?? 0);
              const amount = Number(line.Amount ?? 0);
              const isLast = i === lines.length - 1;

              return (
                <tr key={String(line.Id ?? i)}>
                  <td
                    className={`py-2.5 align-top ${!isLast ? 'border-b border-border/50' : ''}`}
                  >
                    <div className="text-[13.5px] font-medium text-foreground">
                      {description}
                    </div>
                  </td>
                  <td
                    className={`py-2.5 text-center text-[13.5px] text-foreground/80 align-top ${!isLast ? 'border-b border-border/50' : ''}`}
                  >
                    {qty}
                  </td>
                  <td
                    className={`py-2.5 text-right font-mono text-xs text-muted-foreground align-top ${!isLast ? 'border-b border-border/50' : ''}`}
                  >
                    {unitPrice.toFixed(2)}
                  </td>
                  <td
                    className={`py-2.5 text-right font-mono text-[13px] font-medium text-foreground align-top ${!isLast ? 'border-b border-border/50' : ''}`}
                  >
                    {formatCurrency(amount, 2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Subtotal / Tax / Total Due */}
      <div className="flex flex-col items-end my-4 mb-5">
        <div className="w-[220px]">
          <div className="flex justify-between items-center py-1.5 border-b border-border/50">
            <span className="text-[13px] text-muted-foreground">Subtotal</span>
            <span className="font-mono text-[13px] font-medium text-foreground">
              {formatCurrency(totalAmt - totalTax, 2)}
            </span>
          </div>
          {totalTax > 0 && (
            <div className="flex justify-between items-center py-1.5 border-b border-border/50">
              <span className="text-[13px] text-muted-foreground">Tax</span>
              <span className="font-mono text-[13px] font-medium text-foreground">
                {formatCurrency(totalTax, 2)}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center pt-2 mt-1 border-t border-border">
            <span className="text-xs font-medium text-foreground/80">
              {isPaid ? 'Total' : 'Total Due'}
            </span>
            <span
              className={`font-mono text-[13px] font-medium px-1.75 py-0.5 rounded ${
                isPaid
                  ? 'bg-green-500/10 text-green-700'
                  : 'bg-red-500/10 text-red-600'
              }`}
            >
              {formatCurrency(isPaid ? totalAmt : balance, 2)}
            </span>
          </div>
        </div>
      </div>

      {/* Notes & Terms */}
      {(customerMemo || salesTerm) && (
        <div className="border-t border-border/50 pt-4 mb-5 flex gap-8">
          {customerMemo && (
            <div className="flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.8px] text-muted-foreground/70 mb-1">
                Notes
              </div>
              <div className="text-[12.5px] text-muted-foreground leading-[1.6]">
                {customerMemo}
              </div>
            </div>
          )}
          {salesTerm && (
            <div className="flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.8px] text-muted-foreground/70 mb-1">
                Terms
              </div>
              <div className="text-[12.5px] text-muted-foreground leading-[1.6]">
                {salesTerm}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
